import { z } from 'zod';
import { appIdSchema } from './app.js';
import { tenantStatusSchema } from './tenant.js';
/** The operator app's product name (GitHub #162). A brand name: never translated (docs/011). */
export const CONTROL_TOWER_APP_NAME = 'Control Tower';
/** Every Control Tower API route lives under this `/api/web` sub-prefix, behind ONE guard. */
export const CONTROL_TOWER_API_PATH = '/control-tower';
/** The subscription states that count as a tenant's CURRENT plan. CANCELED is not current. */
export const CURRENT_PLAN_STATUSES = ['ACTIVE', 'TRIALING', 'PAST_DUE'];
export const currentPlanStatusSchema = z.enum(CURRENT_PLAN_STATUSES);
/** One app's current PLAN-kind subscription for a tenant. Add-ons are not shown in v1. */
export const controlTowerTenantPlanSchema = z
    .object({
    app: appIdSchema,
    planName: z.string().min(1),
    status: currentPlanStatusSchema,
})
    .strict();
/**
 * `GET /api/web/control-tower/tenants` row. An allow-list (S7): no user, email, session or
 * provider data. `lastActivityAt` is null when the tenant has no qualifying activity
 * (definition: execution-plan/milestones/S52-jecr-control-tower.md "Last activity").
 */
export const controlTowerTenantDTOSchema = z
    .object({
    id: z.string().min(1),
    slug: z.string().min(1),
    name: z.string().min(1),
    status: tenantStatusSchema,
    plans: z.array(controlTowerTenantPlanSchema),
    lastActivityAt: z.string().datetime().nullable(),
    /** When the tenant was created (signed up or was provisioned). */
    createdAt: z.string().datetime(),
    /** ACTIVE (paired, not revoked) devices across every location. */
    pairedKioskCount: z.number().int().nonnegative(),
    /** Every Visitor check-in the tenant has ever recorded, kiosk or front desk. */
    checkInCount: z.number().int().nonnegative(),
    lastCheckInAt: z.string().datetime().nullable(),
})
    .strict();
/**
 * `:tenantId` of `GET /api/web/control-tower/tenants/:tenantId` and `…/:tenantId/members`.
 * The one place Control Tower names a tenant in the path: the caller is a platform SUPERADMIN, so
 * the target tenant is never the token's own, and every query below still carries it (S1).
 */
export const controlTowerTenantParamsSchema = z.object({ tenantId: z.string().min(1).max(64) }).strict();
/** How many days the usage endpoint's daily series and windowed counts cover. */
export const CONTROL_TOWER_USAGE_DAYS = 30;
/** One calendar day in the tenant's own timezone. `date` is `YYYY-MM-DD`. */
export const controlTowerUsageDaySchema = z
    .object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    checkIns: z.number().int().nonnegative(),
})
    .strict();
/** One location of the tenant: its paired kiosks and check-in activity. */
export const controlTowerUsageLocationSchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    pairedKioskCount: z.number().int().nonnegative(),
    /** The most recent heartbeat of any of its paired kiosks; null when none has reported. */
    kioskLastSeenAt: z.string().datetime().nullable(),
    checkInsInWindow: z.number().int().nonnegative(),
    lastCheckInAt: z.string().datetime().nullable(),
})
    .strict();
/**
 * `GET /api/web/control-tower/tenants/:tenantId/usage`: one tenant's adoption at a glance.
 * Aggregates only (S7): no visitor, host or device identity beyond a location's name.
 * The window is the last `CONTROL_TOWER_USAGE_DAYS` days, bucketed in the tenant's timezone.
 */
export const controlTowerTenantUsageDTOSchema = z
    .object({
    timezone: z.string().min(1),
    windowDays: z.number().int().positive(),
    locationCount: z.number().int().nonnegative(),
    pairedKioskCount: z.number().int().nonnegative(),
    checkInsToday: z.number().int().nonnegative(),
    checkInsLast7Days: z.number().int().nonnegative(),
    checkInsInWindow: z.number().int().nonnegative(),
    checkInsAllTime: z.number().int().nonnegative(),
    /** Visits still CHECKED_IN right now. */
    onSiteNow: z.number().int().nonnegative(),
    firstCheckInAt: z.string().datetime().nullable(),
    lastCheckInAt: z.string().datetime().nullable(),
    /** Oldest first, exactly `windowDays` entries, zero days included. */
    daily: z.array(controlTowerUsageDaySchema),
    locations: z.array(controlTowerUsageLocationSchema),
})
    .strict();
//# sourceMappingURL=control-tower.js.map